import React, { useEffect, useMemo, useState } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";


export default function NewNumberTemplateModal({
  show,
  onClose,
  userID,
  baseUrl,
  headers,
  onSent,
}) {
  // UI state
  const [sending, setSending] = useState(false);
  const [tplLoading, setTplLoading] = useState(false);
  const [mediaUploading, setMediaUploading] = useState(false);

  // Inputs
  const [rawNumber, setRawNumber] = useState("");

  // WA numbers (sender phone_no_id)
  const [waNumbers, setWaNumbers] = useState([]);
  const [phoneNoId, setPhoneNoId] = useState("");
  const [multiWA, setMultiWA] = useState(false);

  // Templates
  const [templates, setTemplates] = useState([]);
  const [selectedTplId, setSelectedTplId] = useState("");

  // Vars + media
  const [vars, setVars] = useState([]); // aligned to placeholder indices (1..N)
  const [placeholders, setPlaceholders] = useState([]); // [1,2,3 ...]
  const [headerFormat, setHeaderFormat] = useState(null); // IMAGE|VIDEO|DOCUMENT|null
  const [mediaRef, setMediaRef] = useState({ mediaId: null, mediaUrl: null });

  // helpers
  const headersJson = useMemo(
    () => ({ ...headers, "Content-Type": "application/json" }),
    [headers]
  );

  const sanitizeNumber = (n) => {
    const digits = (n || "").replace(/\D/g, "");
    if (digits.length === 10) return "91" + digits; // default IN if 10 digits
    return digits;
  };

  // open: preload WA numbers
  useEffect(() => {
    if (!show) return;
    (async () => {
      try {
        const res = await fetch(`${baseUrl}/api/Whatsapp/v1.0/getWhatsappNumbers`, {
          method: "POST",
          headers: headersJson,
          body: JSON.stringify({ userid: userID }),
        });
        const json = await res.json();
        if (json?.responseCode === "00") {
          const list = json.response || [];
          setWaNumbers(list);
          if (list.length > 0) setPhoneNoId(list[0].phone_no_id);
          setMultiWA(list.length > 1);
        } else {
          setWaNumbers([]);
          setPhoneNoId("");
        }
      } catch (e) {
        console.error(e);
        setWaNumbers([]);
        setPhoneNoId("");
      }
    })();
  }, [show, baseUrl, headersJson, userID]);

  // load templates on phoneNoId change
  useEffect(() => {
    if (!show || !phoneNoId) return;
    (async () => {
      try {
        setTplLoading(true);
        const res = await fetch(`${baseUrl}/api/Whatsapp/v1.0/getTemplates`, {
          method: "POST",
          headers: headersJson,
          body: JSON.stringify({ userid: userID, phoneNumberId: phoneNoId }),
        });
        const json = await res.json();
        if (json?.responseCode === "00") {
          setTemplates(json.response?.data || []);
        } else {
          setTemplates([]);
        }
      } catch (e) {
        console.error(e);
        setTemplates([]);
      } finally {
        setTplLoading(false);
      }
    })();
  }, [show, phoneNoId, baseUrl, headersJson, userID]);

  const selectedTpl = useMemo(
    () => templates.find((t) => t.id === selectedTplId),
    [templates, selectedTplId]
  );

  // collect placeholders from BODY + header text (if any)
  const collectPlaceholders = (tpl) => {
    if (!tpl) return [];
    const texts = (tpl.components || [])
      .map((c) => (typeof c.text === "string" ? c.text : ""))
      .filter(Boolean);
    const found = new Set();
    texts.forEach((txt) => {
      (txt.match(/\{\{\d+\}\}/g) || []).forEach((m) =>
        found.add(Number(m.replace(/[{}]/g, "")))
      );
    });
    return Array.from(found).sort((a, b) => a - b);
  };

  // derive header format (if header present)
  const getHeaderFormat = (tpl) => {
    const header = (tpl?.components || []).find((c) => c.type === "HEADER");
    return header?.format || null; // IMAGE|VIDEO|DOCUMENT|TEXT|undefined
  };

  // whenever template changes → reset derived states
  useEffect(() => {
    const ph = collectPlaceholders(selectedTpl);
    setPlaceholders(ph);

    // init vars with max index length to keep order 1..N
    const maxIndex = ph.length ? Math.max(...ph) : 0;
    setVars(Array.from({ length: maxIndex }, () => ""));

    const hf = getHeaderFormat(selectedTpl);
    // if header text, treat like no media required
    setHeaderFormat(hf && hf !== "TEXT" ? hf : null);

    setMediaRef({ mediaId: null, mediaUrl: null });
    setMediaUploading(false);
  }, [selectedTplId]); // eslint-disable-line

  const onVarChange = (index1Based, val) => {
    setVars((prev) => {
      const next = [...prev];
      next[index1Based - 1] = val; // keep array index = placeholder-1
      return next;
    });
  };

  // upload header media (success only -> attach)
  const onHeaderMediaFile = async (file) => {
    if (!file || !headerFormat) return;

    // size rule: video ≤ 16 MB
    if (headerFormat === "VIDEO" && file.size > 16 * 1024 * 1024) {
      toast.error("Video must be ≤ 16 MB");
      return;
    }

    try {
      setMediaUploading(true);
      toast.info("Uploading media…");

      // base64 it
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = (reader.result || "").toString();
          const mediaType =
            headerFormat === "IMAGE" ? "img" : headerFormat === "VIDEO" ? "video" : "pdf";

          const res = await fetch(`${baseUrl}/api/Whatsapp/uploadMedia`, {
            method: "POST",
            headers: headersJson,
            body: JSON.stringify({
              userid: userID,
              mediaData: (base64.split(",")[1] || base64),
              mediaType, // server maps this to image/video/document
            }),
          });
          const json = await res.json();

          if (json?.responseCode === "00") {
            // attach ONLY on success
            const mediaId = json?.data?.mediaId || json?.data?.media_id || null;
            const mediaUrl = json?.data?.mediaUrl || json?.data?.media_url || null;
            if (!mediaId && !mediaUrl) {
              toast.error("Upload succeeded but media reference missing");
            } else {
              setMediaRef({ mediaId, mediaUrl });
              toast.success("Media uploaded successfully");
            }
          } else {
            toast.error(json?.responseMessage || "Media upload failed");
          }
        } catch (e) {
          console.error(e);
          toast.error("Upload error");
        } finally {
          setMediaUploading(false);
        }
      };
      reader.readAsDataURL(file);
    } catch (e) {
      console.error(e);
      setMediaUploading(false);
      toast.error("Upload error");
    }
  };

  const acceptAttr =
    headerFormat === "IMAGE"
      ? "image/*"
      : headerFormat === "VIDEO"
      ? "video/*"
      : headerFormat === "DOCUMENT"
      ? ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
      : undefined;

  // send template to new number
  const onSend = async () => {
    const contact = sanitizeNumber(rawNumber);
    if (!contact) return toast.error("Enter a valid mobile number");

    if (!selectedTpl) return toast.error("Select a template");

    // build bodyParams strictly in index order 1..max
    const maxIndex = placeholders.length ? Math.max(...placeholders) : 0;
    const bodyParams = Array.from({ length: maxIndex }, (_, i) => vars[i] || "");

    // header param required if media header
    const headerParams = headerFormat
      ? [mediaRef.mediaId || mediaRef.mediaUrl].filter(Boolean)
      : [];

    if (headerFormat && headerParams.length === 0) {
      return toast.error(`Please upload ${headerFormat.toLowerCase()} header media`);
    }

    const payload = {
      userid: userID,
      secret: "N",
      templateName: selectedTpl.name,
      languageCode: selectedTpl.language || "en",
      messaging_product: "whatsapp",
      contacts: [contact],
      headerParams,
      bodyParams,
      footerParams: [],
      type: selectedTpl.category || "MARKETING",
      execute_type: "N",
      format: headerFormat ? headerFormat.toLowerCase() : "text",
      templateId: selectedTpl.id,
      // optionally include phoneNoId if your API needs it
      // phoneNumberId: phoneNoId,
    };

    try {
      setSending(true);
      const res = await fetch(`${baseUrl}/api/Whatsapp/executeTemplate`, {
        method: "POST",
        headers: headersJson,
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (json?.responseCode === "00") {
        toast.success(json?.responseMessage || "Template sent");
        onClose?.();
        onSent?.();
      } else {
        toast.error(json?.responseMessage || "Failed to send");
      }
    } catch (e) {
      console.error(e);
      toast.error("Error sending template");
    } finally {
      setSending(false);
    }
  };

  // reset when modal closes
  useEffect(() => {
    if (!show) {
      setSending(false);
      setTplLoading(false);
      setMediaUploading(false);
      setSelectedTplId("");
      setPlaceholders([]);
      setVars([]);
      setHeaderFormat(null);
      setMediaRef({ mediaId: null, mediaUrl: null });
      setRawNumber("");
    }
  }, [show]);

  return (
    <Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="w-100 d-flex justify-content-between align-items-center">
          <span>Send Template to New Number</span>
          <div className="d-flex align-items-center gap-2">
            {/* <label className="mb-0">WhatsApp Number</label> */}
            <select
              className="form-select"
              style={{ minWidth: 260 }}
              value={phoneNoId}
              onChange={(e) => setPhoneNoId(e.target.value)}
              disabled={!multiWA}
            >
              <option value="">Select Number</option>
              {waNumbers.map((n, i) => (
                <option key={i} value={n.phone_no_id}>
                  {n.phone_no_id} — ({n.phone_no})
                </option>
              ))}
            </select>
          </div>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Customer number */}
        <Form.Group className="mb-3">
          <Form.Label>Customer Mobile</Form.Label>
          <Form.Control
            type="tel"
            placeholder="Enter mobile (with or without country code)"
            value={rawNumber}
            onChange={(e) => setRawNumber(e.target.value)}
            disabled={sending}
          />
          <div className="small text-muted mt-1">
            Will send to: <b>{sanitizeNumber(rawNumber) || "-"}</b>
          </div>
        </Form.Group>

        {/* Template selector */}
        <Form.Group className="mb-3">
          <Form.Label>Template</Form.Label>
          {tplLoading ? (
            <div className="d-flex align-items-center gap-2">
              <Spinner animation="border" size="sm" /> <span>Loading templates…</span>
            </div>
          ) : templates.length === 0 ? (
            <div className="small text-muted">No templates found.</div>
          ) : (
            <Form.Select
              value={selectedTplId}
              onChange={(e) => setSelectedTplId(e.target.value)}
              disabled={sending}
            >
              <option value="">Select template</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name} ({tpl.language}) — {tpl.category}
                </option>
              ))}
            </Form.Select>
          )}
        </Form.Group>

        {/* Header media if required */}
        {headerFormat && (
          <Form.Group className="mb-3">
            <div className="fw-semibold small mb-1">
              Header media required: {headerFormat}
            </div>
            <div className="d-flex align-items-center gap-2">
              <Form.Control
                type="file"
                accept={acceptAttr}
                onChange={(e) => onHeaderMediaFile(e.target.files?.[0])}
                disabled={mediaUploading || sending}
              />
              {mediaUploading && (
                <span className="d-inline-flex align-items-center gap-2">
                  <Spinner animation="border" size="sm" /> Uploading…
                </span>
              )}
              {!mediaUploading && (mediaRef.mediaId || mediaRef.mediaUrl) && (
                <span className="text-success">Uploaded ✓</span>
              )}
            </div>
            {headerFormat === "VIDEO" && (
              <div className="small text-muted mt-1">Max video size: 16 MB</div>
            )}
          </Form.Group>
        )}

        {/* Variable inputs if template has {{1}}, {{2}}... */}
        {placeholders.length > 0 && (
          <div className="mb-2">
            <div className="fw-semibold small mb-1">Template Variables</div>
            {placeholders.map((index1Based) => (
              <Form.Control
                key={index1Based}
                className="mb-2"
                placeholder={`Value for {{${index1Based}}}`}
                value={vars[index1Based - 1] || ""}
                onChange={(e) => onVarChange(index1Based, e.target.value)}
                disabled={sending}
              />
            ))}
          </div>
        )}
      </Modal.Body>

      <div className="d-flex justify-content-end gap-2 px-3 pb-3">
        <button className="btn btn-danger" onClick={onClose} disabled={sending}>
          Close
        </button>
        <button
          className="gray-button d-inline-flex align-items-center gap-2"
          onClick={onSend}
          disabled={
            sending ||
            !rawNumber ||
            !selectedTplId ||
            mediaUploading ||
            (headerFormat && !(mediaRef.mediaId || mediaRef.mediaUrl))
          }
        >
          {sending && <Spinner animation="border" size="sm" />}
          {sending ? "Sending..." : "Send"}
        </button>
      </div>
    </Modal>
  );
}
